import { CommonModule } from '@angular/common';
import { Component, DestroyRef, inject, input, OnInit, output, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, ReactiveFormsModule } from '@angular/forms';
import { MeilisearchService } from '@web/core/meilisearch/meilisearch.service';
import { CoreStore } from '@web/core/store/core.store';
import { AutoFocusModule } from 'primeng/autofocus';
import { ButtonModule } from 'primeng/button';
import { DividerModule } from 'primeng/divider';
import { SelectButtonModule } from 'primeng/selectbutton';
import { debounceTime, distinctUntilChanged, filter, switchMap } from 'rxjs';
import { TagSelectorComponent } from '../form/tag-selector/tag-selector.component';
import { SearchCollection } from './interfaces/search.interface';
import { SearchItemComponent } from './items/search-item.component';
import { searchQueryBuilder } from './search-query-builder';
import { toSearchEntries } from './search.mapper';
import { SearchEntry } from './types/search-entry.type';

@Component({
  selector: 'app-search',
  imports: [
    ButtonModule,
    SelectButtonModule,
    ReactiveFormsModule,
    CommonModule,
    TagSelectorComponent,
    AutoFocusModule,
    SearchItemComponent,
    DividerModule,
  ],
  template: `
    <div class="flex flex-col h-full overflow-hidden">
      <form [formGroup]="form" class="p-4 flex flex-col gap-4">
        <div class="flex items-center">
          <i class="pi pi-search mr-3 text-lg"></i>
          <input
            formControlName="search"
            type="text"
            placeholder="Cerca attività, commesse, file..."
            class="flex-grow outline-none text-lg"
            [pAutoFocus]="true"
            (keydown.arrowUp)="(true)"
            (keydown.arrowDown)="(true)"
            (keydown.enter)="(true)"
          />
        </div>
        <div class="flex gap-2 overflow-x-auto">
          <app-tag-selector [options]="collections()" formControlName="collections" />
        </div>
      </form>
      <div class="flex flex-col flex-1 overflow-y-auto pb-4">
        @for (result of searchResults(); track $index) {
          <button (click)="close()">
            <app-search-item [item]="result" />
          </button>
          <div class="px-4">
            <p-divider />
          </div>
        }
      </div>
    </div>
  `,
})
export class SearchComponent implements OnInit {
  private readonly fb = inject(FormBuilder);
  private readonly meilisearchService = inject(MeilisearchService);
  private readonly coreStore = inject(CoreStore);
  private readonly destroyRef: DestroyRef = inject(DestroyRef);

  readonly closed = output<null>();

  form = this.fb.group({
    search: this.fb.nonNullable.control<string>('', []),
    collections: this.fb.nonNullable.control<string[]>([], []),
  });

  collections = input.required<SearchCollection[]>();
  searchResults = signal<SearchEntry[]>([]);

  ngOnInit(): void {
    this.form.valueChanges
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        filter((query) => Boolean(query.search)),
        switchMap((query) => {
          const { search, collections = [] } = query ?? {};
          return this.meilisearchService.getInstance().multiSearch({
            queries: searchQueryBuilder({
              search: search!,
              currentUserId: this.coreStore.identity()?.id,
              collections: collections.length
                ? this.collections().filter((col) => collections.includes(col.value))
                : this.collections(),
            }),
            federation: {},
          });
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((response) => {
        this.searchResults.set(toSearchEntries(response.hits));
      });
  }
  close() {
    this.closed.emit(null);
  }
}
